import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "../index";
import { SUPPORTED_LANGUAGES, Language } from "../i18n/constants";
import { translate } from "../i18n/translations";
import { setLanguage } from "../features/language/reducer";

/**
 * Custom hook for accessing language state and translation function
 * @returns {Object} Object containing currentLanguage, dispatch, and translation function t
 */
export const useLanguage = () => {
  const dispatch = useDispatch();
  const languageState = useSelector((state: IRootState) => state.language.value);
  
  // Safely extract currentLanguage, handling various state shapes
  let currentLanguage: Language = "en";
  if (languageState) {
    if (typeof languageState === "string") {
      // If languageState is directly a string
      currentLanguage = (SUPPORTED_LANGUAGES.includes(languageState as Language) ? languageState : "en") as Language;
    } else if (typeof languageState === "object" && languageState !== null) {
      const selected = (languageState as any)?.selected;
      if (typeof selected === "string" && SUPPORTED_LANGUAGES.includes(selected as Language)) {
        currentLanguage = selected as Language;
      }
    }
  }

  /**
   * Translation function that uses the current language
   * @param key - Translation key in dot notation (e.g., "modal.login.title")
   * @param defaultValue - Fallback value if translation not found
   * @returns Translated string
   */
  const t = (key: string, defaultValue?: string): string => {
    try {
      const result = translate(key, currentLanguage, defaultValue);
      // Ensure we always return a string, never an object
      if (typeof result === "string") {
        return result;
      }
      // If result is an object or other type, return the key or defaultValue
      return defaultValue || key;
    } catch (error) {
      console.error("Translation error:", error, "key:", key);
      return defaultValue || key;
    }
  };

  return {
    currentLanguage,
    dispatch,
    t,
  };
};
